import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { authForgot, authReset, formatApiErrorDetail } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-sm text-[#f5f5f5] placeholder-[#666] outline-none focus:border-[#d4af37]";

const Shell = ({ children }) => (
  <div className="hero-particles flex min-h-screen items-center justify-center bg-[#050505] px-6">
    <div className="spotlight pointer-events-none absolute inset-0" />
    <div className="glass-solid gold-border-glow relative z-10 w-full max-w-md rounded-3xl p-8">
      <Link to="/" className="font-display text-2xl font-bold tracking-[0.15em] text-gold">BGM</Link>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await authForgot(email); } catch {}
    setSent(true); setLoading(false);
  };
  return (
    <Shell>
      {sent ? (
        <div className="mt-6 text-center" data-testid="forgot-sent">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-gold" />
          <h1 className="font-display text-xl text-[#f5f5f5]">Check your email</h1>
          <p className="mt-3 font-body text-sm text-[#a3a3a3]">If an account exists for {email}, we've sent a reset link. It expires in 1 hour.</p>
          <Link to="/login" className="mt-6 inline-block font-body text-sm text-gold" data-testid="forgot-back">Back to login</Link>
        </div>
      ) : (
        <>
          <h1 className="mt-5 font-display text-2xl text-[#f5f5f5]">Reset your password</h1>
          <p className="mt-2 font-body text-sm text-[#a3a3a3]">Enter your email and we'll send you a reset link.</p>
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
            <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email" />
            <button type="submit" disabled={loading} data-testid="forgot-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#d4af37] px-6 py-3 font-body text-sm font-semibold text-[#050505] hover:bg-[#f3e5ab] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </button>
          </form>
          <p className="mt-6 text-center font-body text-sm text-[#a3a3a3]"><Link to="/login" className="text-gold">Back to login</Link></p>
        </>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await authReset(token, password); setDone(true); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      {!token ? (
        <p className="mt-6 font-body text-sm text-red-400" data-testid="reset-invalid">Invalid or missing reset link.</p>
      ) : done ? (
        <div className="mt-6 text-center" data-testid="reset-done">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-gold" />
          <h1 className="font-display text-xl text-[#f5f5f5]">Password updated</h1>
          <p className="mt-3 font-body text-sm text-[#a3a3a3]">You can now log in with your new password.</p>
          <button onClick={() => nav("/login")} className="mt-6 rounded-full bg-[#d4af37] px-6 py-2.5 font-body text-sm font-semibold text-[#050505]" data-testid="reset-login">Go to login</button>
        </div>
      ) : (
        <>
          <h1 className="mt-5 font-display text-2xl text-[#f5f5f5]">Set a new password</h1>
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
            <input required type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password" />
            {err && <p className="font-body text-xs text-red-400" data-testid="reset-error">{err}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#d4af37] px-6 py-3 font-body text-sm font-semibold text-[#050505] hover:bg-[#f3e5ab] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}
